import { createEntityAdapter } from "@reduxjs/toolkit";

import apiSlice from "../api/apiSlice";
import { effectiveAtQuery, withBusinessValidity } from "../../utils/businessValidity";

const resourcesAdapter = createEntityAdapter({});
const initialState = resourcesAdapter.getInitialState({});

export const resourcesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getResourcesByCommunityId: builder.query({
      query: ({ communityId, excludeDeleted }) => ({
        url:
          `/communities/${communityId}/resources?` +
          `${excludeDeleted !== undefined ? `&exclude_deleted=${excludeDeleted}` : ""}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result.isError,
      }),
      transformResponse: (responseData) => {
        if (responseData) {
          return resourcesAdapter.setAll(initialState, responseData);
        }
        return resourcesAdapter.setAll(initialState, []);
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: "Resource", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Resource", id })),
          ];
        }
        return [{ type: "Resource", id: "LIST" }];
      },
    }),
    getResourcesByMeterId: builder.query({
      query: ({ communityId, meterId, excludeDeleted = "true" }) => ({
        url: `/communities/${communityId}/resources/meter/${meterId}?exclude_deleted=${excludeDeleted}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result.isError,
      }),
      transformResponse: (responseData) => {
        if (responseData) {
          return resourcesAdapter.setAll(initialState, responseData);
        }
        return resourcesAdapter.setAll(initialState, []);
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: "Resource", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Resource", id })),
          ];
        }
        return [{ type: "Resource", id: "LIST" }];
      },
    }),
    getAvailableResourceMeters: builder.query({
      query: ({ communityId, startDate, endDate, resourceId }) => ({
        url:
          `/communities/${communityId}/resources/available-meters?` +
          `start_date=${encodeURIComponent(startDate)}` +
          `${endDate ? `&end_date=${encodeURIComponent(endDate)}` : ""}` +
          `${resourceId ? `&resource_id=${encodeURIComponent(resourceId)}` : ""}`,
        method: "GET",
      }),
      providesTags: [{ type: "Resource", id: "LIST" }],
    }),
    /* POST */
    addNewResource: builder.mutation({
      query: (resource) => ({
        url: `/communities/${resource.resource_payload.community_id}/resources`,
        method: "POST",
        body: {
          ...resource,
          resource_payload: withBusinessValidity(resource.resource_payload),
          ownerships_payload: resource.ownerships_payload?.map(withBusinessValidity),
        },
      }),
      invalidatesTags: [{ type: "Resource", id: "LIST" }, "Member"],
    }),
    /* UPDATE */
    updateResourceById: builder.mutation({
      query: ({ communityId, assetId, newAsset }) => ({
        url: `/communities/${communityId}/resources/${assetId}`,
        method: "PUT",
        body: {
          ...withBusinessValidity(newAsset),
          ownerships_payload: newAsset.ownerships_payload?.map(withBusinessValidity),
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Resource", id: arg.assetId },
        { type: "Resource", id: "LIST" },
        { type: "BusinessValidity", id: `resource:${arg.assetId}` },
        "Member",
      ],
    }),
    /* DELETE */
    deleteResourceById: builder.mutation({
      query: ({ communityId, resourceId, effectiveAt }) => ({
        url: `/communities/${communityId}/resources/${resourceId}?${effectiveAtQuery(effectiveAt)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: "Resource", id: arg.resourceId },
        { type: "Resource", id: "LIST" },
        "Member",
      ],
    }),
  }),
});

export const {
  useAddNewResourceMutation,
  useGetResourcesByCommunityIdQuery,
  useGetResourcesByMeterIdQuery,
  useGetAvailableResourceMetersQuery,
  useUpdateResourceByIdMutation,
  useDeleteResourceByIdMutation,
} = resourcesApiSlice;

export const { selectAll: selectAllResources } = resourcesAdapter.getSelectors();
